import { Head, Link } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { BrandLogo } from "@/components/brand-logo"
import { Button } from "@/components/ui/button"

export interface SignupSuccessBrand {
  name: string
  logo_url: string | null
  locale: "en" | "fr"
}

export interface SignupSuccessProps {
  brand: SignupSuccessBrand
  /** Full-bleed brand cover; null → dark gradient fallback. */
  cover_image_url: string | null
  first_name: string | null
  /** Inertia visit target — OKS-468 Welcome (`/onboarding/welcome`). */
  continue_url: string
}

/**
 * Signups/Success — OKS-466 immersive "You're on {Brand}'s crew" screen
 * (UX Spec §D / Figma `214:241`). Shown right after the atomic create +
 * sign-in; Continue hands off to the OKS-468 Welcome → first mission.
 *
 * Full-bleed brand cover with a centered logo + check, the headline, and
 * a single Continue CTA (an Inertia visit — the creator is authenticated
 * by now, so this is a normal in-app navigation, NOT an OAuth form).
 *
 * Single passive state (UX Spec §3). Continue is the only affordance.
 */
export default function SignupSuccess({
  brand,
  cover_image_url,
  first_name,
  continue_url,
}: SignupSuccessProps) {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <div
      data-testid="signup-success"
      className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-neutral-900 px-6 py-12 text-center text-white"
    >
      <Head>
        <title>{t("signups.success.head_title")}</title>
      </Head>

      {cover_image_url && (
        <img
          src={cover_image_url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 size-full object-cover opacity-40"
          data-testid="signup-success-cover"
        />
      )}

      <div className="relative z-10 flex max-w-sm flex-col items-center gap-5">
        <BrandLogo
          logoUrl={brand.logo_url}
          name={brand.name}
          className="size-16 rounded-xl ring-2 ring-white/70"
          testId="signup-success-brand-logo"
        />
        <h1
          ref={headingRef}
          tabIndex={-1}
          data-testid="signup-success-heading"
          className="text-2xl font-bold tracking-tight outline-none"
        >
          {t("signups.success.title", { brand: brand.name })}
        </h1>
        <p className="text-white/80" data-testid="signup-success-body">
          {t("signups.success.body", { name: first_name ?? "" })}
        </p>
        <Button
          asChild
          size="lg"
          variant="secondary"
          className="mt-2 w-full"
          data-testid="signup-success-continue"
        >
          <Link href={continue_url}>{t("signups.success.continue")}</Link>
        </Button>
      </div>
    </div>
  )
}
